// src/components/managed-sources/MicrosoftAdsAuthStep.tsx

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Loader,
  Paper,
  Radio,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import {
  IconBrandGoogle,
  IconBrandWindows,
  IconExternalLink,
  IconPlugConnected,
  IconX,
} from "@tabler/icons-react";

import {
  getMicrosoftAuthUrl,
  exchangeMicrosoftToken,
  listMicrosoftManagers,
  listMicrosoftAccounts,
} from "@/api/managedSources";
import { listAuthorizations } from "@/api/authorizations";
import type { AuthorizationDTO } from "@/types/authorizations";
import {
  authorizationRenderOption,
  authorizationSelectData,
  isUsableByMe,
} from "@/components/authorizations/authorizationOwnership";
import type {
  MicrosoftAdsAccount,
  MicrosoftAdsManager,
} from "@/types/managedSources";

/** Which ad accounts under the chosen manager the pipeline ingests. */
export type MicrosoftAccountSelectionMode = "ALL" | "SPECIFIC";

/** How much of the Microsoft Advertising surface the transforms model. Must
 *  stay in step with the backend's `COVERAGE_PRESETS`
 *  (integrations/connectors/microsoft_ads.py). */
export type MicrosoftCoveragePreset = "minimal" | "standard" | "broad";

/** Which identity the customer signs in with. A Microsoft Advertising account can
 *  be backed by EITHER a Microsoft identity or a Google account; the backend
 *  builds the matching OAuth URL from this choice and carries it in the signed
 *  state, so the token exchange is identical either way. */
export type MicrosoftIdentityProvider = "microsoft" | "google";

/** The two sign-in options, in the order the wizard offers them. The backend
 *  returns its availability list in this same preference order, so the first
 *  available entry is what gets pre-selected. An option this environment cannot
 *  complete a sign-in with is shown greyed out with an explanation rather than
 *  hidden — a customer who is looking for "Sign in with Microsoft" should see
 *  WHY it can't be picked, not an unexplained absence (the same choice the
 *  wizard already makes for "coming soon" data categories). */
const IDENTITY_PROVIDER_OPTIONS: {
  value: MicrosoftIdentityProvider;
  label: string;
  unavailableHint: string;
}[] = [
  {
    value: "microsoft",
    label: "Sign in with Microsoft",
    unavailableHint:
      "Signing in with a Microsoft account isn't available yet. Use a Google account instead.",
  },
  {
    value: "google",
    label: "Sign in with Google",
    unavailableHint:
      "Signing in with a Google account isn't available yet. Use a Microsoft account instead.",
  },
];

/** The provider the form starts on: the first one this environment can actually
 *  complete a sign-in with. Falls back to Microsoft when the backend says
 *  nothing (a backend that predates the field, or a connector without the
 *  choice) — the behaviour that shipped before availability existed. */
export function firstAvailableIdentityProvider(
  available?: string[] | null,
): MicrosoftIdentityProvider {
  if (!Array.isArray(available)) return "microsoft";
  const known = IDENTITY_PROVIDER_OPTIONS.map((o) => o.value);
  return (
    (available.find((p) =>
      known.includes(p as MicrosoftIdentityProvider),
    ) as MicrosoftIdentityProvider | undefined) ?? "microsoft"
  );
}

type Props = {
  authorizationId: string | null;
  /** Which identity providers this environment can complete a sign-in with,
   *  from the connector catalogue (`available_identity_providers`). Passed down
   *  from the wizard, which already has the connector — deliberately NOT loaded
   *  here: the authorization load below swallows its own errors, so riding on
   *  that call would render a network failure as "no providers". `undefined`/
   *  `null` means no gating. Gates NEW connections only; existing
   *  authorizations stay visible and usable either way. */
  availableIdentityProviders?: string[] | null;
  /** The selected MANAGER account's customer_id (names the raw dataset). */
  customerId: string;
  /** The manager's display name — lifted so the review + default source name can
   *  show it instead of a bare id. Reported together with the id to keep the two
   *  from drifting apart. */
  customerName: string;
  accountSelectionMode: MicrosoftAccountSelectionMode;
  accountIds: string[];
  coveragePreset: MicrosoftCoveragePreset;
  onAuthorizationChange: (id: string | null) => void;
  onCustomerIdChange: (id: string, name: string) => void;
  onAccountSelectionModeChange: (mode: MicrosoftAccountSelectionMode) => void;
  onAccountIdsChange: (ids: string[]) => void;
  onCoveragePresetChange: (preset: MicrosoftCoveragePreset) => void;
  /** The "Connection name" the customer typed here, lifted so the wizard can
   *  propose it as the source name instead of overwriting it with a composed
   *  default. Fires on every keystroke; stays empty when an existing
   *  authorization is reused. */
  onConnectionNameChange?: (name: string) => void;
  onValidated: (valid: boolean) => void;
};

/** Microsoft's AccountLifeCycleStatus. Only "Active" accounts can be ingested —
 *  everything else (Pause, Draft, Inactive) is shown but not selectable, so a
 *  customer looking for an account sees WHY it can't be picked rather than an
 *  unexplained absence. Missing status is treated as active (lenient), matching
 *  GoogleAdsAuthStep's handling of an unknown status. */
function isAccountActive(a: MicrosoftAdsAccount): boolean {
  return !a.status || a.status.toLowerCase() === "active";
}

export default function MicrosoftAdsAuthStep({
  authorizationId,
  availableIdentityProviders,
  customerId,
  customerName,
  accountSelectionMode,
  accountIds,
  coveragePreset,
  onAuthorizationChange,
  onCustomerIdChange,
  onAccountSelectionModeChange,
  onAccountIdsChange,
  onCoveragePresetChange,
  onConnectionNameChange,
  onValidated,
}: Props) {
  // Existing authorizations
  const [existingAuths, setExistingAuths] = useState<AuthorizationDTO[]>([]);
  const [loadingAuths, setLoadingAuths] = useState(true);

  // Mode
  const [mode, setMode] = useState<"existing" | "new">("existing");

  // New auth form
  const [authName, setAuthName] = useState("");
  const [oauthLoading, setOauthLoading] = useState(false);
  const [oauthError, setOauthError] = useState("");
  // Which identity the customer signs in with. Passed to the auth-url call; the
  // backend carries it in the signed state, so the exchange is unchanged.
  const [identityProvider, setIdentityProvider] = useState<
    MicrosoftIdentityProvider
  >(() => firstAvailableIdentityProvider(availableIdentityProviders));

  // Manager accounts (Microsoft calls these "customers")
  const [managers, setManagers] = useState<MicrosoftAdsManager[]>([]);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [managersError, setManagersError] = useState("");

  // Advertiser accounts under the chosen manager
  const [accounts, setAccounts] = useState<MicrosoftAdsAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [accountsError, setAccountsError] = useState("");

  // OAuth popup + exchange bookkeeping
  const popupRef = useRef<Window | null>(null);
  const exchangedRef = useRef(false);
  // The signed state + PKCE verifier from /auth-url. Both ride the authenticated
  // API — the verifier NEVER travels on the OAuth redirect (that is the point of
  // PKCE) — and are echoed back to /exchange-token. Microsoft requires the state
  // where Google ignores it: the backend verifies an account-bound signed state.
  const stateRef = useRef<string | null>(null);
  const verifierRef = useRef<string | null>(null);

  // Identity-provider availability. An array is an answer from the backend; an
  // absent value means the backend said nothing, and nothing is gated — the
  // behaviour that shipped before the field existed. Gating applies to NEW
  // connections only: an existing authorization stays selectable and refreshable
  // even in an environment that can no longer create one.
  const identityGatingKnown = Array.isArray(availableIdentityProviders);
  const isProviderAvailable = (p: MicrosoftIdentityProvider) =>
    !identityGatingKnown || availableIdentityProviders!.includes(p);
  const noIdentityProviderAvailable =
    identityGatingKnown &&
    !IDENTITY_PROVIDER_OPTIONS.some((o) => isProviderAvailable(o.value));

  // Keep the selection on a provider that can actually complete a sign-in: the
  // connector catalogue can change under this step (account switch, refetch).
  useEffect(() => {
    if (!Array.isArray(availableIdentityProviders)) return;
    if (availableIdentityProviders.includes(identityProvider)) return;
    setIdentityProvider(
      firstAvailableIdentityProvider(availableIdentityProviders),
    );
  }, [availableIdentityProviders, identityProvider]);

  // Reset the whole manager/account selection — used whenever the connection
  // itself changes, so a manager id from one authorization never bleeds into
  // another's account tree.
  const resetSelection = useCallback(() => {
    onCustomerIdChange("", "");
    onAccountSelectionModeChange("ALL");
    onAccountIdsChange([]);
  }, [onCustomerIdChange, onAccountSelectionModeChange, onAccountIdsChange]);

  // Load existing Microsoft Ads authorizations
  useEffect(() => {
    async function load() {
      setLoadingAuths(true);
      try {
        const auths = await listAuthorizations({ type: "MICROSOFT_ADS" });
        setExistingAuths(auths);
        // Only rows I may use count (ADR-0329): a list of colleagues'
        // personal connections alone starts on "Create new connection".
        if (!auths.some(isUsableByMe)) {
          setMode("new");
        }
      } catch {
        // Non-fatal
      } finally {
        setLoadingAuths(false);
      }
    }
    load();
  }, []);

  // Validate: an authorization, a chosen manager, and — when SPECIFIC — at least
  // one selected account. Coverage always has a default, so it never blocks.
  useEffect(() => {
    const hasAccountSelection =
      accountSelectionMode === "ALL" ||
      (accountSelectionMode === "SPECIFIC" && accountIds.length > 0);
    onValidated(!!authorizationId && !!customerId && hasAccountSelection);
  }, [
    authorizationId,
    customerId,
    accountSelectionMode,
    accountIds,
    onValidated,
  ]);

  // Load manager accounts when the authorization is set.
  useEffect(() => {
    if (!authorizationId) {
      setManagers([]);
      return;
    }
    async function loadManagers() {
      setLoadingManagers(true);
      setManagersError("");
      try {
        const result = await listMicrosoftManagers(authorizationId!);
        setManagers(result.customers);
        // Auto-select a lone manager — there is no choice to make.
        if (result.customers.length === 1) {
          const m = result.customers[0];
          onCustomerIdChange(m.customer_id, m.customer_name);
        }
      } catch (e: any) {
        // The backend's 422 message is ALREADY customer-friendly and actionable
        // (e.g. identity-provider mismatch guidance for a work/personal-account
        // mixup). Show it verbatim rather than hiding it behind a generic line.
        setManagersError(
          e?.message ||
            "We couldn't load your Microsoft Advertising manager accounts.",
        );
      } finally {
        setLoadingManagers(false);
      }
    }
    loadManagers();
    // onCustomerIdChange is intentionally omitted: the wizard passes it as an
    // inline arrow (new each render), so including it would re-fetch managers on
    // every render. Mirrors FacebookAuthStep's ad-account load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authorizationId]);

  // Load advertiser accounts once a manager is chosen.
  useEffect(() => {
    if (!authorizationId || !customerId) {
      setAccounts([]);
      return;
    }
    async function loadAccounts() {
      setLoadingAccounts(true);
      setAccountsError("");
      try {
        const result = await listMicrosoftAccounts(authorizationId!, customerId);
        setAccounts(result.accounts);
      } catch (e: any) {
        // Same verbatim-message rule as the manager load above.
        setAccountsError(
          e?.message ||
            "We couldn't load the ad accounts for this manager account.",
        );
      } finally {
        setLoadingAccounts(false);
      }
    }
    loadAccounts();
  }, [authorizationId, customerId]);

  // Listen for OAuth popup messages
  const handleOAuthMessage = useCallback(
    async (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "microsoft_oauth_callback") return;

      const { code, state, error } = event.data;
      popupRef.current = null;

      if (error) {
        setOauthError(`Microsoft authorization failed: ${error}`);
        setOauthLoading(false);
        return;
      }

      if (!code) {
        setOauthError("No authorization code received from Microsoft.");
        setOauthLoading(false);
        return;
      }

      // The callback's state must match the value stashed when we opened the
      // popup. The backend re-verifies its own signed, account-bound state — this
      // client check just rejects a mismatched/replayed callback before spending
      // an exchange on it.
      if (!stateRef.current || state !== stateRef.current) {
        setOauthError(
          "We couldn't verify the Microsoft sign-in. Please try again.",
        );
        setOauthLoading(false);
        return;
      }

      // Prevent duplicate exchange calls (React Strict Mode / double events)
      if (exchangedRef.current) return;
      exchangedRef.current = true;

      try {
        const result = await exchangeMicrosoftToken(
          code,
          stateRef.current,
          verifierRef.current ?? "",
          authName.trim(),
        );
        // A fresh connection starts with a clean manager/account slate.
        resetSelection();
        onAuthorizationChange(result.authorization_id);
      } catch (e: any) {
        setOauthError(e?.message || "Failed to exchange authorization code");
      } finally {
        setOauthLoading(false);
      }
    },
    [authName, onAuthorizationChange, resetSelection],
  );

  useEffect(() => {
    window.addEventListener("message", handleOAuthMessage);
    return () => window.removeEventListener("message", handleOAuthMessage);
  }, [handleOAuthMessage]);

  async function handleConnectMicrosoft() {
    if (!authName.trim()) return;
    exchangedRef.current = false;
    setOauthLoading(true);
    setOauthError("");

    try {
      const { url, state, code_verifier } =
        await getMicrosoftAuthUrl(identityProvider);
      stateRef.current = state;
      verifierRef.current = code_verifier;
      const width = 600;
      const height = 700;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      popupRef.current = window.open(
        url,
        "microsoft_oauth",
        `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no`,
      );

      const checkInterval = setInterval(() => {
        if (popupRef.current?.closed) {
          clearInterval(checkInterval);
          if (oauthLoading) {
            setOauthLoading(false);
          }
        }
      }, 1000);
    } catch (e: any) {
      stateRef.current = null;
      verifierRef.current = null;
      setOauthError(e?.message || "Failed to get authorization URL");
      setOauthLoading(false);
    }
  }

  function handleSelectExistingAuth(authId: string | null) {
    // Switching connections invalidates the current manager/account picks.
    resetSelection();
    onAuthorizationChange(authId);
  }

  function toggleAccount(id: string, checked: boolean) {
    onAccountIdsChange(
      checked ? [...accountIds, id] : accountIds.filter((x) => x !== id),
    );
  }

  const activeAccountIds = accounts.filter(isAccountActive).map((a) => a.account_id);
  const allActiveSelected =
    activeAccountIds.length > 0 &&
    activeAccountIds.every((id) => accountIds.includes(id));

  const existingAuthOptions = authorizationSelectData(existingAuths);

  if (loadingAuths) {
    return (
      <Stack align="center" justify="center" py={60}>
        <Loader size="sm" />
        <Text size="sm" c="dimmed">
          Loading authorizations...
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="lg">
      {/* Hero section */}
      <Group gap="md" wrap="nowrap">
        <ThemeIcon size={48} radius="xl" variant="light" color="blue">
          <IconBrandWindows size={26} />
        </ThemeIcon>
        <Stack gap={2}>
          <Text fw={700} size="lg">
            Connect Microsoft Advertising
          </Text>
          <Text size="sm" c="dimmed">
            Link your Microsoft Advertising account to sync campaign performance
            data.
          </Text>
        </Stack>
      </Group>

      {/* Mode selector */}
      {existingAuths.length > 0 && (
        <SegmentedControl
          value={mode}
          onChange={(v) => {
            setMode(v as "existing" | "new");
            if (v === "new") {
              resetSelection();
              onAuthorizationChange(null);
            }
          }}
          data={[
            { label: "Use existing connection", value: "existing" },
            { label: "Create new connection", value: "new" },
          ]}
          fullWidth
        />
      )}

      {/* Existing authorization */}
      {mode === "existing" && existingAuths.length > 0 && (
        <Stack gap="md">
          <Select
            label="Microsoft Advertising connection"
            placeholder="Select a saved connection"
            data={existingAuthOptions}
            renderOption={authorizationRenderOption(existingAuths)}
            value={authorizationId}
            onChange={handleSelectExistingAuth}
            size="md"
          />
        </Stack>
      )}

      {/* New authorization. When this environment can complete no sign-in at
          all, the form would be a dead end — say so instead of rendering it. */}
      {mode === "new" && noIdentityProviderAvailable && (
        <Alert
          color="yellow"
          title="You can't add a Microsoft Advertising connection yet"
        >
          Signing in to Microsoft Advertising isn't available in Verity yet.
          {existingAuths.length > 0
            ? " You can still use a connection that was added earlier."
            : " Please try again later, or contact support if you need this now."}
        </Alert>
      )}

      {mode === "new" && !noIdentityProviderAvailable && (
        <Stack gap="md">
          <TextInput
            label="Connection name"
            placeholder="e.g. My Microsoft Ads"
            value={authName}
            onChange={(e) => {
              setAuthName(e.currentTarget.value);
              onConnectionNameChange?.(e.currentTarget.value);
            }}
            required
            size="md"
            disabled={!!authorizationId}
          />

          {!authorizationId && (
            <Radio.Group
              label="Choose how you sign in to Microsoft Advertising"
              value={identityProvider}
              onChange={(v) =>
                setIdentityProvider(v as MicrosoftIdentityProvider)
              }
            >
              <Stack gap="xs" mt="xs">
                {IDENTITY_PROVIDER_OPTIONS.map((opt) => {
                  const unavailable = !isProviderAvailable(opt.value);
                  return (
                    <Radio
                      key={opt.value}
                      value={opt.value}
                      disabled={unavailable}
                      label={
                        <Group gap="xs" wrap="nowrap">
                          <Text size="sm">{opt.label}</Text>
                          {unavailable && (
                            <Badge variant="light" color="gray" size="xs">
                              Not available yet
                            </Badge>
                          )}
                        </Group>
                      }
                      description={
                        unavailable ? opt.unavailableHint : undefined
                      }
                    />
                  );
                })}
              </Stack>
            </Radio.Group>
          )}

          {!authorizationId && (
            <Button
              variant="outline"
              leftSection={
                identityProvider === "google" ? (
                  <IconBrandGoogle size={18} />
                ) : (
                  <IconBrandWindows size={18} />
                )
              }
              rightSection={<IconExternalLink size={14} />}
              onClick={handleConnectMicrosoft}
              loading={oauthLoading}
              disabled={!authName.trim() || !isProviderAvailable(identityProvider)}
              size="md"
              fullWidth
            >
              {identityProvider === "google"
                ? "Sign in with Google"
                : "Sign in with Microsoft"}
            </Button>
          )}

          {!authorizationId && (
            <Text size="xs" c="dimmed">
              Microsoft Advertising doesn't offer a read-only permission, so
              signing in grants management access. Verity only ever reads your
              data — it never changes your campaigns.
            </Text>
          )}

          {oauthError && (
            <Alert color="red" icon={<IconX size={16} />}>
              {oauthError}
            </Alert>
          )}
        </Stack>
      )}

      {/* Manager account selector (shown after auth) */}
      {authorizationId && (
        <>
          {loadingManagers ? (
            <Group gap="xs">
              <Loader size="xs" />
              <Text size="sm" c="dimmed">
                Loading manager accounts...
              </Text>
            </Group>
          ) : managersError ? (
            <Alert color="red" icon={<IconX size={16} />}>
              {managersError}
            </Alert>
          ) : managers.length > 0 ? (
            <Select
              label="Manager account"
              description="Pick the Microsoft Advertising manager account to sync from."
              placeholder="Select a manager account"
              data={managers.map((m) => ({
                value: m.customer_id,
                label: `${m.customer_name} (${m.account_count} account${
                  m.account_count === 1 ? "" : "s"
                })`,
              }))}
              value={customerId || null}
              onChange={(v) => {
                const m = managers.find((x) => x.customer_id === v);
                onCustomerIdChange(v || "", m?.customer_name || "");
                // The account picks belong to the previous manager's tree.
                onAccountSelectionModeChange("ALL");
                onAccountIdsChange([]);
              }}
              size="md"
              searchable
            />
          ) : (
            <Alert color="orange">
              No manager accounts were found for this connection. Make sure your
              Microsoft Advertising user has access to at least one account.
            </Alert>
          )}
        </>
      )}

      {/* Ad-account selection + coverage (shown after a manager is chosen) */}
      {authorizationId && customerId && (
        <>
          <Radio.Group
            label="Which ad accounts?"
            value={accountSelectionMode}
            onChange={(v) =>
              onAccountSelectionModeChange(v as MicrosoftAccountSelectionMode)
            }
          >
            <Stack gap="xs" mt="xs">
              <Radio
                value="ALL"
                label="All accounts (including future ones)"
              />
              <Radio value="SPECIFIC" label="Specific accounts" />
            </Stack>
          </Radio.Group>

          {accountSelectionMode === "SPECIFIC" && (
            <>
              {loadingAccounts ? (
                <Group gap="xs">
                  <Loader size="xs" />
                  <Text size="sm" c="dimmed">
                    Loading ad accounts...
                  </Text>
                </Group>
              ) : accountsError ? (
                <Alert color="red" icon={<IconX size={16} />}>
                  {accountsError}
                </Alert>
              ) : accounts.length > 0 ? (
                <Table
                  striped
                  withTableBorder
                  verticalSpacing="xs"
                  horizontalSpacing="sm"
                >
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th style={{ width: 40 }}>
                        <Checkbox
                          aria-label="Select all active accounts"
                          checked={allActiveSelected}
                          indeterminate={
                            !allActiveSelected && accountIds.length > 0
                          }
                          disabled={activeAccountIds.length === 0}
                          onChange={(e) =>
                            onAccountIdsChange(
                              e.currentTarget.checked ? activeAccountIds : [],
                            )
                          }
                        />
                      </Table.Th>
                      <Table.Th>Account</Table.Th>
                      <Table.Th>Number</Table.Th>
                      <Table.Th>Currency</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {accounts.map((a) => {
                      const active = isAccountActive(a);
                      return (
                        <Table.Tr key={a.account_id}>
                          <Table.Td>
                            <Checkbox
                              aria-label={a.account_name}
                              checked={accountIds.includes(a.account_id)}
                              disabled={!active}
                              onChange={(e) =>
                                toggleAccount(a.account_id, e.currentTarget.checked)
                              }
                            />
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">
                              {a.account_name}
                              {!active && (
                                <Text span size="sm" c="dimmed">
                                  {" "}
                                  — {a.status.toLowerCase()}
                                </Text>
                              )}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" ff="monospace" c="dimmed">
                              {a.account_number}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{a.currency_code}</Text>
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              ) : (
                <Alert color="orange">
                  No ad accounts were found under this manager account.
                </Alert>
              )}
            </>
          )}

          <Radio.Group
            label="Data coverage"
            description="Choose how much of your Microsoft Advertising data to model."
            value={coveragePreset}
            onChange={(v) =>
              onCoveragePresetChange(v as MicrosoftCoveragePreset)
            }
          >
            <Stack gap="sm" mt="xs">
              <Radio
                value="minimal"
                label={
                  <div>
                    <Text size="sm" fw={500}>
                      Minimal
                    </Text>
                    <Text size="xs" c="dimmed">
                      Core performance, keywords &amp; Performance Max
                    </Text>
                  </div>
                }
              />
              <Radio
                value="standard"
                label={
                  <div>
                    <Text size="sm" fw={500}>
                      Standard{" "}
                      <Text span size="xs" c="dimmed">
                        (Recommended)
                      </Text>
                    </Text>
                    <Text size="xs" c="dimmed">
                      Adds impression share, audiences, geo &amp; conversions
                    </Text>
                  </div>
                }
              />
              <Radio
                value="broad"
                label={
                  <div>
                    <Text size="sm" fw={500}>
                      Broad
                    </Text>
                    <Text size="xs" c="dimmed">
                      Everything, including extended breakdowns &amp; hourly data
                    </Text>
                  </div>
                }
              />
            </Stack>
          </Radio.Group>

          <Text size="xs" c="dimmed">
            Performance Max asset-level data has only 30 days of history at
            Microsoft and can't be backfilled further, even with a longer
            backfill window.
          </Text>
        </>
      )}

      {/* Connected confirmation */}
      {authorizationId && customerId && (
        <Paper
          withBorder
          p="md"
          radius="md"
          style={{
            borderColor: "var(--mantine-color-green-4)",
            backgroundColor: "var(--mantine-color-green-0)",
          }}
        >
          <Group gap="sm">
            <ThemeIcon size={32} color="green" variant="light" radius="xl">
              <IconPlugConnected size={16} />
            </ThemeIcon>
            <Stack gap={2}>
              <Text size="sm" fw={600}>
                Connected
              </Text>
              <Text size="xs" c="dimmed">
                {customerName || customerId} ·{" "}
                {accountSelectionMode === "ALL"
                  ? "All accounts"
                  : `${accountIds.length} account${
                      accountIds.length === 1 ? "" : "s"
                    } selected`}
              </Text>
            </Stack>
          </Group>
        </Paper>
      )}
    </Stack>
  );
}
